import type { FC } from 'react';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import type { AssignmentServiceRequest } from 'src/apis/assignment-service/use-assignment-service-root';
import { useOptionalAssignmentService } from 'src/apis/assignment-service/use-optional-assignment-service';
import { useOptionalAuthorizationService } from 'src/apis/authorization-service/use-authorization-service';
import { useCapacityManagement } from 'src/apis/capacity-management/use-capacity-management';
import { useOptionalLngMarketingRoot } from 'src/apis/lng-marketing/use-lng-marketing-root';
import type { OrganisationServiceRequest } from 'src/apis/organisation-service/use-organisation-service';
import { useOptionalOrganisationService } from 'src/apis/organisation-service/use-organisation-service';
import { useMapPrototype } from 'src/apis/prototype-map/use-map-prototype';
import { useRegularFcfsRoot } from 'src/apis/regular-fcfs/use-regular-fcfs-root';
import { useRemitReportingEntrypoint } from 'src/apis/remit-reporting/use-remit-reporting-entrypoint';
import { isTransportActive } from 'src/components/layout/app-shell/is-transport-active';
import { NewDesignToggle } from 'src/components/layout/app-shell/new-design-toggle';
import { HeaderPrimary } from 'src/components/layout/header/primary';
import { HeaderSecondary } from 'src/components/layout/header/secondary';
import { isDashboardActive } from 'src/components/layout/header/secondary-nav-dashboard';
import { isGdmActive } from 'src/components/layout/header/secondary-nav-gdm';
import { isLngActive } from 'src/components/layout/header/secondary-nav-lng';
import { isMarketMessagesActive } from 'src/components/layout/header/secondary-nav-market-messages';
import { isReportingActive } from 'src/components/layout/header/secondary-nav-reporting';
import { isStorageActive } from 'src/components/layout/header/secondary-nav-storage';
import { useMobileNav } from 'src/components/layout/header/use-mobile-nav';
import { LayoutHeader } from 'src/components/layout/layout';
import { SimpleHeader } from 'src/components/layout/simple-page-layout';
import { AngularModalDimmer } from 'src/components/overlay/angular-modal-dimmer';
import { stickyHeaderId } from 'src/components/scroll-management';
import type { Successful } from 'src/hooks/use-axios';
import { useDirectFcfsFlags } from 'src/hooks/use-direct-fcfs-flags';
import { useHeaderHeightMeasure } from 'src/hooks/use-header-height';
import { useIsSnamAdmin } from 'src/pages/snam-bid-collector/use-is-snam-admin';

export const Header: FC = () => {
  const { pathname, hash } = useLocation();

  const authorizationService = useOptionalAuthorizationService();
  const organisationService = useOptionalOrganisationService();
  const capacityManagementRoot = useCapacityManagement();
  const regularFcfsService = useRegularFcfsRoot();
  const directFcfsService = useDirectFcfsFlags();
  const lngMarketingRoot = useOptionalLngMarketingRoot();
  const assignmentService = useOptionalAssignmentService();
  const remitReportingEntrypoint = useRemitReportingEntrypoint();
  // ! Implementation for the Map Prototype
  const mapPrototype = useMapPrototype();

  const activeSection = useMemo(() => {
    const dashboard = isDashboardActive(pathname, hash);
    const transport = isTransportActive(pathname, hash);
    const storage = isStorageActive(pathname, hash);
    const lng = isLngActive(pathname);
    const reporting = isReportingActive(pathname, hash);
    const gdm = isGdmActive(pathname);
    const marketMessages = isMarketMessagesActive(pathname);
    return {
      dashboard,
      transport,
      storage,
      reporting,
      gdm,
      lng,
      marketMessages,
    };
  }, [pathname, hash]);

  const ref = useHeaderHeightMeasure();
  const isSnamAdmin = useIsSnamAdmin();
  const mobileNav = useMobileNav();

  if (
    (authorizationService.response === null &&
      authorizationService.error === null) ||
    (lngMarketingRoot.response === null && lngMarketingRoot.error === null) ||
    (organisationService.response === null &&
      organisationService.error === null) ||
    (assignmentService.response === null && assignmentService.error === null) ||
    !remitReportingEntrypoint.loaded ||
    !regularFcfsService.loaded ||
    isSnamAdmin === undefined ||
    (mapPrototype?.response === null && mapPrototype.error === null)
  )
    return (
      <LayoutHeader id={stickyHeaderId} ref={ref}>
        <SimpleHeader />
      </LayoutHeader>
    );

  return (
    <LayoutHeader
      id={stickyHeaderId}
      ref={ref}
      fullscreen={mobileNav.value !== null}
      onClick={(e) => {
        if (mobileNav.value === null) return;
        // when the mobile nav is open the LayoutHeader is covering the whole screen,
        // even if it is invisble. when the user directly clicks on the LayoutHeader (which means the
        // user actually wanted to click _outside_ the mobile nav) we close the mobile nav.
        if (e.target instanceof HTMLElement && e.target.id === stickyHeaderId)
          mobileNav.setValue(null);
      }}
    >
      <AngularModalDimmer>
        <NewDesignToggle />

        <header>
          <HeaderPrimary
            activeSection={activeSection}
            authorizationService={
              authorizationService.response ? authorizationService : undefined
            }
            organisationService={
              organisationService.response === null
                ? undefined
                : (organisationService as Successful<OrganisationServiceRequest>)
            }
            capacityManagementService={capacityManagementRoot}
            regularFcfsService={regularFcfsService.response?.data}
            directFcfsService={directFcfsService.data}
            assignmentService={
              assignmentService.response === null
                ? undefined
                : (assignmentService as Successful<AssignmentServiceRequest>)
            }
            remitReportingService={remitReportingEntrypoint.response?.data}
            mobileNav={mobileNav}
            mapPrototype={mapPrototype?.response ? mapPrototype : undefined}
          />
          <HeaderSecondary
            isSnamAdmin={isSnamAdmin}
            activeSection={activeSection}
            mobileNav={mobileNav}
            assignmentService={
              assignmentService.response === null
                ? undefined
                : (assignmentService as Successful<AssignmentServiceRequest>)
            }
          />
        </header>
        <div
          id="react-loading-bar"
          className={`fixed h-px w-[0%] bg-legacy-brand opacity-100`}
        />
      </AngularModalDimmer>
    </LayoutHeader>
  );
};
